import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Snackbar from "@mui/material/Snackbar";
import PortfolioTable from "./PortfolioTable";
import CurrentCoinTable from "./CurrentCoinTable";
import Stack from "@mui/material/Stack";
import { getDatabase, ref, onValue, set } from "firebase/database";
import { getAuth } from "firebase/auth";
import { styled } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";
import MuiAlert from "@mui/material/Alert";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Item = styled(Paper)(({ theme, override, isProfit }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: override ? "#fff" : theme.palette.text.primary,
  backgroundColor: override ? (isProfit ? "#4caf50" : "#ef5350") : "#fff",
}));

export default function CoinTracker(props) {
  const [coins, setCoins] = useState([]);
  const [defaultCoin, setDefaultCoin] = useState("");
  const [trades, setTrades] = useState([]);
  const [units, setUnits] = useState(10);
  const [openToast, setOpenToast] = React.useState(false);
  const [openDeleteToast, setOpenDeleteToast] = React.useState(false);

  const handleToastClick = () => {
    setOpenToast(true);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenToast(false);
  };

  const handleDeleteToastClick = () => {
    setOpenDeleteToast(true);
  };

  const handleDeleteClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenDeleteToast(false);
  };
  useEffect(() => {
    fetch(
      "https://api.coingecko.com/api/v3/coins/markets?vs_currency=USD&order=market_cap_desc&per_page=100&page=1&sparkline=false"
    )
      .then((resp) => resp.json())
      .then((resp) => {
        resp && setCoins(resp);
      });
  }, []);
  const fetchTrades = () => {
    const db = getDatabase();
    const auth = getAuth();
    const portfolioID = auth?.currentUser?.uid;
    const tradeRef = ref(db, "portfolio/" + portfolioID + "/trades");
    onValue(tradeRef, (snapshot) => {
      const data = snapshot.val();
      console.log("Updating trades", data);
      setTrades(data ?? []);
    });
  };
  useEffect(() => {
    fetchTrades();
  }, []);
  const coinDropdownValues = coins?.map((item) => {
    return {
      label: item.name,
      ...item,
    };
  });
  const onAdd = () => {
    console.log("Add coin", defaultCoin);
    const coinBuyPrice = defaultCoin?.current_price;
    const updatedTrades = [
      ...trades,
      {
        tradeID: uuidv4(),
        coin: defaultCoin,
        buyPrice: coinBuyPrice,
        buyTime: new Date().toISOString(),
        units: units,
        totalValue: coinBuyPrice * units,
      },
    ];
    const db = getDatabase();
    const auth = getAuth();
    const portfolioID = auth?.currentUser?.uid;
    const tradeRef = ref(db, "portfolio/" + portfolioID + "/trades");
    set(tradeRef, updatedTrades);
    // setTrades(updatedTrades);
    handleToastClick();
    fetchTrades();
    setDefaultCoin("");
    setUnits(10);
  };
  const findCurrentPrice = (name) => {
    return coins?.find((item) => item.name === name)?.current_price;
  };
  const enhancedTrades = trades?.map((trade) => {
    const currentPrice = findCurrentPrice(trade?.coin?.name);
    const currentValue = (trade?.units * currentPrice);
    const isProfit = currentValue > trade?.totalValue;
    const diff = currentValue - trade?.totalValue;
    const perCent = ((diff / currentValue) * 100).toFixed(2);
    return {
      currentPrice: currentPrice,
      currentValue: currentValue,
      isProfit,
      diff,
      perCent,
      ...trade,
    };
  }).sort((a, b) => new Date(b.buyTime).getTime() - new Date(a.buyTime).getTime());
  const handleUnitsChange = (e) => {
    setUnits(e.target.value);
  };
  const gains = enhancedTrades
    .reduce((prev, current) => prev + current?.diff, 0)
    .toFixed(2);
  const totalValue = enhancedTrades
    .reduce((prev, current) => prev + current?.totalValue, 0)
    .toFixed(2);
  const portfolio = {
    currentValue: enhancedTrades
      .reduce((prev, current) => {
        return prev + current?.currentValue;
      }, 0)
      .toFixed(2),
    totalValue: totalValue,
    gains: gains,
    percent: ((gains / totalValue) * 100).toFixed(2),
  };
  const onDeleteTrade = (tradeId) => {
    console.log("Delete Trade", tradeId);
    const updatedTrades = trades.filter((trade) => trade.tradeID !== tradeId);
    const db = getDatabase();
    const auth = getAuth();
    const portfolioID = auth?.currentUser?.uid;
    const tradeRef = ref(db, "portfolio/" + portfolioID + "/trades");
    set(tradeRef, updatedTrades);
    fetchTrades();
    handleDeleteToastClick();
  };
  return (
    <Grid
      container
      direction="column"
      justifyContent="left"
      alignItems="center"
      spacing={2}
      style={{ maxWidth: "100%" }}
    >
      <Grid
        item
        container
        direction="row"
        justifyContent="left"
        alignItems="center"
        xs={6}
        spacing={2}
        style={{ width: "100%" }}
      >
        <Grid item style={{ maxWidth: "100%", paddingLeft : '4px' }} xs={8} md={3}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }} >
            Trade Entry
          </Typography>
        </Grid>
        <Grid item style={{ maxWidth: "100%" }}>
          <Autocomplete
            disablePortal
            id="combo-box-demo"
            options={coinDropdownValues}
            sx={{ minWidth: "10rem" }}
            renderInput={(params) => <TextField {...params} label="Add Coin" />}
            value={defaultCoin}
            onChange={(event, newValue) => {
              setDefaultCoin(newValue);
            }}
          />
        </Grid>
        <Grid item style={{ maxWidth: "100%" }}>
          <Select
            id="demo-simple-select"
            value={units}
            label="Units"
            onChange={handleUnitsChange}
          >
            <MenuItem value={10}>Ten</MenuItem>
            <MenuItem value={20}>Twenty</MenuItem>
            <MenuItem value={30}>Thirty</MenuItem>
          </Select>
        </Grid>
        <Grid item style={{ maxWidth: "100%" }}>
          <Button onClick={onAdd} disabled={!defaultCoin}>
            Add
          </Button>
          <Snackbar
            open={openToast}
            autoHideDuration={6000}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            key="add-toast"
          >
            <Alert onClose={handleClose} severity="info" sx={{ width: "100%" }}>
              Trade has been added
            </Alert>
          </Snackbar>
        </Grid>
      </Grid>
      {defaultCoin && (
        <Grid item xs={10} style={{ maxWidth: "100%" }}>
          <CurrentCoinTable row={defaultCoin} />
        </Grid>
      )}
      <Grid item style={{ width: "100%" }}>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Portfolio
        </Typography>
      </Grid>
      <Grid item style={{ width: "100%" }}>
        <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} >
          <Item>Current Value {portfolio.currentValue}</Item>
          <Item>Total Invested {portfolio.totalValue}</Item>
          <Item override={true} isProfit={portfolio.gains > 0}>
            Total Gains {portfolio.gains}
          </Item>
          <Item override={true} isProfit={portfolio.gains > 0}>
            Percent Gains {portfolio.percent}
          </Item>
        </Stack>
      </Grid>
      <Grid item xs={10} style={{ maxWidth: "100%" }}>
        <PortfolioTable rows={enhancedTrades} onDeleteTrade={onDeleteTrade} />
        <Snackbar
          open={openDeleteToast}
          autoHideDuration={6000}
          onClose={handleDeleteClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          key="delete-toast"
        >
          <Alert
            onClose={handleDeleteClose}
            severity="error"
            sx={{ width: "100%" }}
          >
            Trade has been deleted
          </Alert>
        </Snackbar>
      </Grid>
      <Grid item xs={10} style={{ maxWidth: "100%" }}>
        <Typography variant="caption" display="block" gutterBottom align="left">
          *Prices in consideration is USD
        </Typography>
      </Grid>
    </Grid>
  );
}
